import { useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthLayout } from '../auth/AuthLayout';
import { RelinkControl } from './RelinkControl';
import { driveMoveNote, type DriveMoveDetail, type DriveMoveNote } from './drive-move-note';
import { AUDIT_NOT_LOGGED_NOTE } from './identity-review';

/** What the last parish reassign said: the move itself, then its Drive half,
 *  then — only when the worker could not write its audit row — the audit note
 *  (cca-gmdm). That order is the relink notice's: the note comes last. */
interface ReassignNotice {
  lead: string;
  drive: DriveMoveNote | null;
  auditNotLogged: boolean;
}

interface QueueItem {
  id: string;
  applicantName: string;
  jobTitle: string;
  submittedAt: string | null;
  status: string | null;
  /** Link to the applicant's Drive folder — present once approved (folder exists). */
  driveFolderUrl?: string | null;
}

interface PipelineItem extends QueueItem {
  approvedAt?: string | null;
  lodgedAt?: string | null;
  receivedAt?: string | null;
  cleared?: boolean;
  expiresAt?: string | null;
}

interface InviteItem {
  id: string;
  email: string;
  name: string;
  invitedAt: string;
  lastSentAt: string;
  sendCount: number;
}

/**
 * A held application, as its parish is allowed to see it (R2, registrar's
 * ruling 2026-09-17): the name the applicant typed, the role, the date. There
 * is deliberately no fourth field — an approver must never learn WHY it is
 * held or WHO else is on record for that email.
 */
interface WithRegistrarItem {
  applicantName: string;
  jobTitle: string;
  submittedAt: string;
}

interface QueueResponse {
  parishName: string;
  parishFolderUrl?: string | null;
  queue: QueueItem[];
  /** `null` = the read FAILED; `[]` = nothing is held. They are not the same
   *  fact and must not render the same (ADR-064 §7, §10). */
  withRegistrar?: WithRegistrarItem[] | null;
  /** The held list hit its cap, so what is shown is not all of it. */
  withRegistrarTruncated?: boolean;
  invitations?: InviteItem[];
  pipeline?: {
    awaitingLodgement: PipelineItem[];
    awaitingResult: PipelineItem[];
    results: PipelineItem[];
  };
}

type PageState = 'loading' | 'loaded' | 'forbidden' | 'error';

export function VettingQueuePage(): JSX.Element {
  const { parishSlug } = useParams<{ parishSlug: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<PageState>('loading');
  const [isRegistrar, setIsRegistrar] = useState(false);
  const [parishName, setParishName] = useState('');
  const [parishFolderUrl, setParishFolderUrl] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [withRegistrar, setWithRegistrar] = useState<WithRegistrarItem[] | null>([]);
  const [withRegistrarTruncated, setWithRegistrarTruncated] = useState(false);
  /** What the last identity move said, announced once for the page. Registrar
   *  only — an approver never sees the control that writes it. */
  const [relinkNotice, setRelinkNotice] = useState('');
  /** What the last parish reassign said, incl. a Drive failure (cca-vz2o) —
   *  announced in the same status line. Registrar only, like the relink's. */
  const [reassignNotice, setReassignNotice] = useState<ReassignNotice | null>(null);
  /** What the last cancellation said (cca-gmdm): who was cancelled, then — only
   *  when the worker could not write the `cancel_application` row, the only
   *  record of who cancelled — the audit note. Unlike the two above, an approver
   *  sees it: a successful cancel always says so (Miranda r4 S1), so the page is
   *  not silent until something goes wrong. */
  const [cancelNotice, setCancelNotice] = useState('');
  const [invitations, setInvitations] = useState<InviteItem[]>([]);
  const [pipeline, setPipeline] = useState<QueueResponse['pipeline']>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  // 1.12 affirmation modal — opens when Approve is clicked
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approverName, setApproverName] = useState('');
  const [affirmed, setAffirmed] = useState(false);
  // Registrar-only: vetting-enabled parishes, for the "Reassign parish" picker.
  const [parishes, setParishes] = useState<{ slug: string; name: string }[]>([]);
  const [queueOpen, setQueueOpen] = useState(true);

  const returnTo = `/parish/${parishSlug ?? ''}`;

  // `silent` refreshes the data in place (no full-page Loading swap) so inline
  // state — the invite box's "sent" note, open sections — survives the reload.
  async function loadQueue(opts?: { silent?: boolean }) {
    if (!opts?.silent) setState('loading');
    try {
      const res = await fetch(`/api/vetting/parish/${parishSlug}/queue`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
        return;
      }
      if (res.status === 403) {
        setState('forbidden');
        return;
      }
      if (!res.ok) {
        setState('error');
        return;
      }
      const data = (await res.json()) as QueueResponse;
      setParishName(data.parishName);
      setParishFolderUrl(data.parishFolderUrl ?? null);
      setQueue(data.queue);
      // `?? []` would turn the server's "could not read" into "nothing held" —
      // the exact collapse this field was split to prevent. Only an ABSENT
      // field (an older worker) means "no section".
      setWithRegistrar(data.withRegistrar === undefined ? [] : data.withRegistrar);
      setWithRegistrarTruncated(data.withRegistrarTruncated === true);
      setInvitations(data.invitations ?? []);
      setPipeline(data.pipeline);
      setState('loaded');
    } catch {
      setState('error');
    }
  }

  /** Empty the page's status line as an action starts, so the next notice is a
   *  change — and announced — even when its text repeats the last one (cca-vz2o),
   *  and no earlier notice is left standing under a later action (Miranda r4 S2). */
  function clearNotices() {
    setRelinkNotice('');
    setReassignNotice(null);
    setCancelNotice('');
  }

  // A reassign moves the record OUT of this parish, so the row (and its
  // control) goes with the reload — the sentence lives here, at page level.
  // Silent reload: a full one would swap the page for "Loading…" and tear down
  // the status line that was just announced (the relink's reason too).
  function onReassigned(notice: ReassignNotice) {
    clearNotices();
    setReassignNotice(notice);
    void loadQueue({ silent: true });
  }

  useEffect(() => {
    void loadQueue();
    // Registrar emulation banner — approvers never see it
    fetch('/api/vetting/auth/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { role?: string } | null) => {
        const reg = d?.role === 'registrar';
        setIsRegistrar(reg);
        if (reg) {
          // Pull the parish list once, for the Reassign picker.
          fetch('/api/vetting/registrar/parishes', { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .then((pd: { parishes?: { slug: string; name: string }[] } | null) =>
              setParishes(pd?.parishes ?? []),
            )
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [parishSlug]);

  async function handleApprove(recordId: string) {
    setBusy(recordId);
    setActionError(null);
    clearNotices();
    try {
      const res = await fetch(`/api/vetting/parish/${parishSlug}/record/${recordId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ approverName: approverName.trim(), affirmed }),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(d.error ?? 'Approval failed. Please try again.');
      } else {
        setApprovingId(null);
        setApproverName('');
        setAffirmed(false);
        await loadQueue();
      }
    } catch {
      setActionError('An error occurred. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  async function handleCancel(item: { id: string; applicantName: string }) {
    const recordId = item.id;
    if (!cancelReason.trim()) return;
    setBusy(recordId);
    setActionError(null);
    clearNotices();
    try {
      const res = await fetch(`/api/vetting/parish/${parishSlug}/record/${recordId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        setActionError(d.error ?? 'Cancellation failed. Please try again.');
      } else {
        const d = (await res.json().catch(() => ({}))) as { auditLogged?: boolean };
        setCancellingId(null);
        setCancelReason('');
        // cca-gmdm: the cancellation stands either way, and says so. Only a
        // strict `false` (the worker's own failure signal) adds the audit note;
        // an older worker's absent field adds nothing.
        setCancelNotice(
          `Cancelled ${item.applicantName}'s application.` +
            (d.auditLogged === false ? ` ${AUDIT_NOT_LOGGED_NOTE}` : ''),
        );
        // Silent, as onReassigned's: a full reload swaps the page for
        // "Loading…" and tears down the status region the note was just put in.
        await loadQueue({ silent: true });
      }
    } catch {
      setActionError('An error occurred. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  if (state === 'loading') {
    return (
      <AuthLayout>
        <p style={{ color: 'var(--text2)', textAlign: 'center', marginTop: 48 }}>Loading…</p>
      </AuthLayout>
    );
  }

  if (state === 'forbidden') {
    return (
      <AuthLayout>
        <div style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
          <p style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Not authorised</p>
          <p style={{ fontSize: 14, color: 'var(--text2)' }}>
            Your account is not listed as a vetting approver for this parish. Contact the CCAANZ
            Registrar if you believe this is an error.
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'error') {
    return (
      <AuthLayout>
        <div style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
          <p style={{ color: 'var(--red)' }}>Could not load the vetting queue. Please try again.</p>
          <button
            onClick={() => void loadQueue()}
            className="btn btn-ghost"
            style={{ marginTop: 16 }}
          >
            Retry
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout showSignOut>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {isRegistrar && (
          <div
            style={{
              background: 'var(--brand-lt)',
              border: '1px solid var(--brand)',
              borderRadius: 6,
              padding: '10px 16px',
              fontSize: 13,
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>Registrar view</strong> — this is exactly what this parish's approver sees.
            </span>
            <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <a
                href={`/form/${parishSlug ?? ''}?paper=1`}
                style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}
              >
                Enter a paper application
              </a>
              <a
                href="/registrar"
                style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}
              >
                ← Back to Registrar dashboard
              </a>
            </span>
          </div>
        )}
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          Police Vetting — {parishName}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 32 }}>
          Applications awaiting your review and identity verification.
        </p>

        {actionError && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid var(--red)',
              borderRadius: 6,
              padding: '12px 16px',
              fontSize: 13,
              color: 'var(--red)',
              marginBottom: 20,
            }}
          >
            {actionError}
          </div>
        )}

        {/* In the document before it can gain text (SC 4.1.3), like the
            registrar dashboard's. The relink and reassign notices are
            registrar-only (their controls are only mounted for the registrar);
            the cancellation's notice (cca-gmdm) reaches approvers too. */}
        {/* ONE always-mounted live region. A Drive warning is NOT drawn inside
            the green .idr-status wash (cca-vz2o, Miranda): it is its own line in
            the danger role colour, led by a text label so it is not colour-only. */}
        <div
          role="status"
          aria-live="polite"
          style={{ margin: relinkNotice || reassignNotice || cancelNotice ? '0 0 16px' : 0 }}
        >
          <p className="idr-status" style={{ margin: 0 }}>
            {relinkNotice}
            {cancelNotice}
            {reassignNotice && (
              <>
                {reassignNotice.lead}
                {reassignNotice.drive && !reassignNotice.drive.warn && (
                  <> {reassignNotice.drive.text}</>
                )}
                {/* Last, as on the relink notice (Miranda r4 P1). A Drive WARNING
                    keeps its own danger line below, so the note stays here. */}
                {reassignNotice.auditNotLogged && <> {AUDIT_NOT_LOGGED_NOTE}</>}
              </>
            )}
          </p>
          {reassignNotice?.drive?.warn && (
            <p className="idr-danger" style={{ fontSize: 13, margin: '8px 0 0' }}>
              <strong>Needs attention:</strong> {reassignNotice.drive.text}
              {/* Its own line, out of the sentence (Miranda r2 S2); the visible
                  text is the start of the accessible name (SC 2.5.3). */}
              {reassignNotice.drive.folderUrl && (
                <span className="idr-link-line">
                  <a
                    className="idr-link"
                    href={reassignNotice.drive.folderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open the old Drive folder (opens in a new tab)"
                  >
                    Open the old Drive folder ↗
                  </a>
                </span>
              )}
            </p>
          )}
        </div>

        <WithRegistrarSection items={withRegistrar} truncated={withRegistrarTruncated} />

        <CollapsibleHead
          title="Awaiting your approval"
          count={queue.length}
          open={queueOpen}
          onToggle={() => setQueueOpen((o) => !o)}
        />
        {!queueOpen ? null : queue.length === 0 ? (
          <div
            style={{
              background: 'white',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '40px 24px',
              textAlign: 'center',
            }}
          >
            <p style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
              No applications pending
            </p>
            <p style={{ fontSize: 13, color: 'var(--text2)' }}>
              New applications submitted by your parish members will appear here.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sortByOldestSubmitted(queue).map((item) => (
              <div
                key={item.id}
                style={{
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '20px 24px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: 'var(--text)',
                        marginBottom: 2,
                      }}
                    >
                      {item.applicantName}
                    </p>
                    <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>
                      {item.jobTitle}
                    </p>
                    {item.submittedAt && (
                      <p style={{ fontSize: 12, color: 'var(--text2)' }}>
                        Submitted{' '}
                        {new Date(item.submittedAt).toLocaleDateString('en-NZ', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                    <p style={{ margin: '8px 0 0', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <a
                        href={`/api/vetting/application/${item.id}`}
                        target="_blank"
                        rel="noopener"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--brand)',
                          textDecoration: 'none',
                        }}
                      >
                        View / print application ↗
                      </a>
                      <a
                        href={`/api/vetting/application/${item.id}/edit-link`}
                        target="_blank"
                        rel="noopener"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--brand)',
                          textDecoration: 'none',
                        }}
                      >
                        Edit ↗
                      </a>
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      className="btn btn-primary"
                      disabled={busy === item.id}
                      onClick={() => {
                        setApprovingId(item.id);
                        setApproverName('');
                        setAffirmed(false);
                        setActionError(null);
                      }}
                    >
                      Approve
                    </button>
                    <button
                      className="btn btn-ghost"
                      disabled={busy === item.id}
                      onClick={() => {
                        setCancellingId(item.id);
                        setCancelReason('');
                        setActionError(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {cancellingId === item.id && (
                  <div
                    style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}
                  >
                    <label
                      htmlFor="vetting-cancel-reason"
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--text)',
                        marginBottom: 6,
                      }}
                    >
                      Reason for cancellation
                    </label>
                    {/* maxLength: the worker's CANCEL_REASON_MAX (parish-dashboard.ts),
                        which refuses a longer reason with a 400 (cca-gmdm r4). */}
                    <textarea
                      id="vetting-cancel-reason"
                      value={cancelReason}
                      maxLength={1000}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="e.g. Applicant withdrew / incorrect parish / duplicate submission"
                      className="input"
                      style={{ height: 72, resize: 'vertical', marginBottom: 10 }}
                      disabled={busy === item.id}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          setCancellingId(null);
                          setCancelReason('');
                        }}
                        disabled={busy === item.id}
                      >
                        Back
                      </button>
                      <button
                        className="btn"
                        style={{
                          background: 'var(--red)',
                          color: 'white',
                          border: 'none',
                        }}
                        disabled={busy === item.id || !cancelReason.trim()}
                        onClick={() => void handleCancel(item)}
                      >
                        {busy === item.id ? 'Cancelling…' : 'Confirm cancellation'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Registrar-only corrections, exactly as ReassignControl has
                    always been. The MOVE belongs here as well as on the
                    lodgement queue: the server allows it from 'Submitted by
                    Applicant' too (ADR-064 §9), and this is the state a wrong
                    link is normally spotted in — the approver is looking at a
                    consent form with one name on it and a card with another.
                    Mounting it only on the lodgement queue made the Link
                    confirmation's promise ("you can move it until it is lodged
                    with Police") untrue for the whole time the application sits
                    here, which is most of its life. */}
                {isRegistrar && (
                  <div
                    style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}
                  >
                    <ReassignControl
                      recordId={item.id}
                      applicantName={item.applicantName}
                      parishes={parishes}
                      onAttempt={clearNotices}
                      onDone={onReassigned}
                    />
                    <div style={{ marginTop: 10 }}>
                      <RelinkControl
                        recordId={item.id}
                        parishName={parishName}
                        applicantName={item.applicantName}
                        onAttempt={clearNotices}
                        onMoved={(message) => {
                          setRelinkNotice(message);
                          // Silent: the row's name follows the new Contact, and
                          // a full reload would swap the page for "Loading…",
                          // tearing down the notice that was just announced.
                          void loadQueue({ silent: true });
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <SendFormBox slug={parishSlug ?? ''} onSent={() => void loadQueue({ silent: true })} />

        <InvitationsSection
          slug={parishSlug ?? ''}
          items={invitations}
          onChanged={() => void loadQueue({ silent: true })}
        />

        {pipeline && (
          <PipelineSections
            awaitingLodgement={pipeline.awaitingLodgement}
            awaitingResult={pipeline.awaitingResult}
            results={pipeline.results}
            isRegistrar={isRegistrar}
            parishes={parishes}
            onReassignAttempt={clearNotices}
            onReassigned={onReassigned}
          />
        )}

        {parishFolderUrl && <ParishFolderCard url={parishFolderUrl} parishName={parishName} />}

        {approvingId && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(17, 24, 39, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
              padding: 16,
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 10,
                padding: '26px 28px',
                maxWidth: 520,
                width: '100%',
                boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
              }}
            >
              <h2
                style={{ fontSize: 17, fontWeight: 600, color: 'var(--brand)', margin: '0 0 6px' }}
              >
                Identity verification &amp; declaration
              </h2>
              <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 14px' }}>
                Approving stamps section 1.12 of the application and attaches your electronic
                signature. The completed form is saved to the parish application folder
                automatically a few moments later.
              </p>

              <label className="input-label" htmlFor="vetting-approver-name">
                Your full name
              </label>
              <input
                id="vetting-approver-name"
                className="input"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                placeholder="Full legal name"
                disabled={busy === approvingId}
                style={{ marginBottom: 14 }}
              />

              <label
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  fontSize: 13,
                  lineHeight: 1.6,
                  marginBottom: 16,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={affirmed}
                  onChange={(e) => setAffirmed(e.target.checked)}
                  disabled={busy === approvingId}
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <span>
                  I confirm that I have sighted a primary ID, a secondary ID, and that one form of
                  ID is photographic (and evidence of any name change, if applicable). I have
                  complied and will comply with the Authorised Agency Agreement and the Vetting
                  Bill; I am satisfied as to the identity of the vetting subject; I have obtained
                  the authorisation of the vetting subject (and of a parent or guardian for
                  applicants aged 14 or 15). Ticking this box constitutes my electronic signature.
                </span>
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-ghost"
                  disabled={busy === approvingId}
                  onClick={() => setApprovingId(null)}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!approverName.trim() || !affirmed || busy === approvingId}
                  onClick={() => void handleApprove(approvingId)}
                >
                  {busy === approvingId ? (
                    <>
                      <span className="busy-spinner" />
                      Approving…
                    </>
                  ) : (
                    'Confirm approval'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <p style={{ marginTop: 32, fontSize: 12, color: 'var(--text2)', textAlign: 'center' }}>
          Before approving, verify the applicant's identity in person. Contact{' '}
          <a href="mailto:nzpv@confessinganglicans.nz" className="btn-link">
            nzpv@confessinganglicans.nz
          </a>{' '}
          with questions.
        </p>
      </div>
    </AuthLayout>
  );
}

/** Direct line to the parish's vetting folder in Google Drive. */
function ParishFolderCard({ url, parishName }: { url: string; parishName: string }): JSX.Element {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      style={{ textDecoration: 'none', display: 'block', marginTop: 24 }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-dk) 100%)',
          borderRadius: 10,
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          boxShadow: '0 2px 10px rgba(22, 61, 49, 0.25)',
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            background: 'rgba(255, 255, 255, 0.14)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M3 7a2 2 0 0 1 2-2h4.2a2 2 0 0 1 1.6.8l.9 1.2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"
              fill="white"
              fillOpacity="0.92"
            />
            <path d="M3 10h18v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z" fill="white" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15.5, color: 'white' }}>
            Parish vetting folder
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
            Signed applications and Police results for {parishName}, filed automatically.
          </p>
        </div>
        <span
          style={{
            background: 'white',
            color: 'var(--brand)',
            fontWeight: 700,
            fontSize: 13,
            padding: '9px 18px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Open in Drive ↗
        </span>
      </div>
    </a>
  );
}

/** Box for inviting a prospective applicant — copy the form link or email it directly. */
function SendFormBox({ slug, onSent }: { slug: string; onSent?: () => void }): JSX.Element {
  const formUrl = `https://vetting.confessinganglicans.nz/form/${slug}`;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'duplicate' | 'error'>('idle');
  const [copied, setCopied] = useState(false);

  function copyLink() {
    void navigator.clipboard.writeText(formUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  async function sendInvite() {
    setStatus('sending');
    try {
      const res = await fetch(`/api/vetting/parish/${slug}/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ email: email.trim(), name: name.trim() }),
      });
      if (!res.ok) {
        setStatus('error');
        return;
      }
      const data = (await res.json()) as { ok?: boolean; duplicate?: boolean };
      if (data.duplicate) {
        setStatus('duplicate');
      } else {
        setStatus('sent');
        setName('');
        setEmail('');
        onSent?.();
      }
    } catch {
      setStatus('error');
    }
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '20px 24px',
        marginTop: 24,
      }}
    >
      <p style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', marginBottom: 2 }}>
        Invite someone to complete a police vet
      </p>
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
        Email your parish's consent form link to a prospective applicant, or copy it to share
        yourself.
      </p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <code
          style={{
            fontSize: 12,
            background: 'var(--brand-lt)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '6px 10px',
            wordBreak: 'break-all',
          }}
        >
          {formUrl}
        </code>
        <button className="btn btn-ghost btn-sm" onClick={copyLink} style={{ flexShrink: 0 }}>
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 140px' }}>
          <label className="input-label" htmlFor="vetting-invite-name">
            Name (optional)
          </label>
          <input
            id="vetting-invite-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name"
            disabled={status === 'sending'}
          />
        </div>
        <div style={{ flex: '2 1 220px' }}>
          <label className="input-label" htmlFor="vetting-invite-email">
            Email address
          </label>
          <input
            id="vetting-invite-email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'sent' || status === 'duplicate' || status === 'error')
                setStatus('idle');
            }}
            placeholder="applicant@example.com"
            disabled={status === 'sending'}
          />
        </div>
        <button
          className="btn btn-primary"
          style={{ flexShrink: 0 }}
          disabled={!emailValid || status === 'sending'}
          onClick={() => void sendInvite()}
        >
          {status === 'sending' ? 'Sending…' : 'Send invitation'}
        </button>
      </div>

      {status === 'sent' && (
        <p style={{ fontSize: 13, color: 'var(--green)', marginTop: 10 }}>
          ✓ Invitation sent. The applicant will receive the form link by email.
        </p>
      )}
      {status === 'duplicate' && (
        <p style={{ fontSize: 13, color: 'var(--amber)', marginTop: 10 }}>
          An invitation was already sent to this address in the last 24 hours — not re-sent.
        </p>
      )}
      {status === 'error' && (
        <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 10 }}>
          Could not send the invitation. Please try again, or copy the link and email it yourself.
        </p>
      )}
    </div>
  );
}

/** Invitations sent but not yet completed — chase with a reminder or mark off. */
function InvitationsSection({
  slug,
  items,
  onChanged,
}: {
  slug: string;
  items: InviteItem[];
  onChanged: () => void;
}): JSX.Element | null {
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [note, setNote] = useState<{ id: string; kind: 'reminded' | 'duplicate' | 'error' } | null>(
    null,
  );

  if (items.length === 0) return null;

  async function act(id: string, action: 'remind' | 'dismiss') {
    setBusyId(id);
    setNote(null);
    try {
      const res = await fetch(`/api/vetting/parish/${slug}/invite/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) {
        setNote({ id, kind: 'error' });
        return;
      }
      if (action === 'remind') {
        const data = (await res.json()) as { duplicate?: boolean };
        setNote({ id, kind: data.duplicate ? 'duplicate' : 'reminded' });
        onChanged(); // refresh "last reminded"
      } else {
        onChanged(); // row drops off the list
      }
    } catch {
      setNote({ id, kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ marginTop: 28 }}>
      <CollapsibleHead
        title="Invited — awaiting application"
        count={items.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && (
        <>
          <p style={{ fontSize: 12, color: 'var(--text2)', margin: '0 0 10px' }}>
            These people were emailed the consent form but haven't completed it yet. Send a
            reminder, or mark an entry off if it's no longer needed. Entries clear automatically
            when the form is submitted.
          </p>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '14px 20px',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{item.name || item.email}</span>
                  {item.name && (
                    <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 10 }}>
                      {item.email}
                    </span>
                  )}
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text2)' }}>
                    Invited {fmtNZDate(item.invitedAt)}
                    {item.sendCount > 1 &&
                      ` · reminded ${item.sendCount - 1}× (last ${fmtNZDate(item.lastSentAt)})`}
                  </p>
                  {note?.id === item.id && (
                    <p
                      style={{
                        margin: '4px 0 0',
                        fontSize: 12,
                        color:
                          note.kind === 'reminded'
                            ? 'var(--green)'
                            : note.kind === 'duplicate'
                              ? 'var(--amber)'
                              : 'var(--red)',
                      }}
                    >
                      {note.kind === 'reminded'
                        ? '✓ Reminder sent.'
                        : note.kind === 'duplicate'
                          ? 'Already emailed in the last 24 hours — not re-sent.'
                          : 'Something went wrong. Please try again.'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    className="btn btn-primary"
                    disabled={busyId === item.id}
                    onClick={() => void act(item.id, 'remind')}
                  >
                    {busyId === item.id ? 'Working…' : 'Send reminder'}
                  </button>
                  <button
                    className="btn btn-ghost"
                    disabled={busyId === item.id}
                    onClick={() => void act(item.id, 'dismiss')}
                    title="Remove from this list without sending anything"
                  >
                    Mark off
                  </button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function fmtNZDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Parse a date for sorting; missing/unparseable dates get `missing` so they sink
 *  to a deliberate end of the list rather than an arbitrary spot. */
function ts(iso: string | null | undefined, missing: number): number {
  if (!iso) return missing;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? missing : t;
}

function byName(a: PipelineItem, b: PipelineItem): number {
  return a.applicantName.localeCompare(b.applicantName);
}

function sortByOldestSubmitted<T extends QueueItem>(items: T[]): T[] {
  return [...items].sort(
    (a, b) =>
      ts(a.submittedAt, Number.MAX_SAFE_INTEGER) - ts(b.submittedAt, Number.MAX_SAFE_INTEGER),
  );
}

/** "8 September 2026", in New Zealand time — a submission made late on the 8th
 *  NZ time is still the 8th here, whatever UTC calls it (UX-8). */
function fmtNzLongDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * "With the Registrar" — applications of this parish's that are held for
 * identity review (R2, registrar's ruling 2026-09-17).
 *
 * The parish is told an application exists and is being looked at, so nobody
 * chases something they cannot see. It is told NOTHING else: no reason, no
 * hint, no CRM person, no email — an approver learning that a mailbox already
 * belongs to somebody would recreate the very disclosure this whole stream
 * exists to stop, one step further out.
 *
 * There are deliberately no controls on these rows. The row is information,
 * and the helper line says plainly that nothing is being asked of them.
 */
function WithRegistrarSection({
  items,
  truncated,
}: {
  items: WithRegistrarItem[] | null;
  truncated: boolean;
}): JSX.Element | null {
  // A failed read is NOT an empty queue (ADR-064 §7). An approver shown nothing
  // concludes nothing is held and chases the applicant for an application the
  // Registrar already has. One plain line, no list, nothing to press.
  if (items === null) {
    return (
      <p style={{ fontSize: 13, color: 'var(--text2)', margin: '8px 0 20px' }}>
        Couldn&rsquo;t load &ldquo;With the Registrar&rdquo; just now &mdash; if an application of
        yours is missing from the list below, the Registrar&rsquo;s queue still has it.
      </p>
    );
  }
  if (items.length === 0) return null;
  return (
    <section
      className="idr-wr"
      aria-labelledby="with-registrar-heading"
      style={{ marginBottom: 28 }}
    >
      <h2
        id="with-registrar-heading"
        style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px' }}
      >
        With the Registrar
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 8px' }}>
        The Registrar is checking a detail on these before they come to you. There's nothing you
        need to do.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((item, i) => (
          // The server sends no id for these rows on purpose — a quarantine id
          // is a registrar's handle on a registrar's queue — so the key is the
          // row's own facts plus its position. The list is read-only and never
          // reorders within a render, so that is stable.
          <li className="idr-wr-row" key={`${item.applicantName}-${item.submittedAt}-${i}`}>
            <span className="idr-wr-name">{item.applicantName}</span>
            <span className="idr-wr-sub">
              {item.jobTitle} · Submitted {fmtNzLongDate(item.submittedAt)}
            </span>
            <span className="pill pill-neutral">With the Registrar</span>
          </li>
        ))}
      </ul>
      {/* A capped list that does not say it is capped reads as the whole list.
          One shared office mailbox in a glut can hold far more than a page. */}
      {truncated && (
        <p style={{ fontSize: 13, color: 'var(--color-muted)', margin: '8px 0 0' }}>
          Only the {items.length} oldest are shown. The Registrar has more of yours than this.
        </p>
      )}
    </section>
  );
}

/** Section heading whose count pill is the click target to show/hide the body —
 *  same roll-up behaviour as the Registrar dashboard's CollapsibleHead. */
function CollapsibleHead({
  title,
  count,
  open,
  onToggle,
  right,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  right?: JSX.Element | false | undefined;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: '2px solid var(--brand)',
        flexWrap: 'wrap',
      }}
    >
      {/* The same repair the Registrar dashboard's CollapsibleHead already
          carries: a screen-reader user heard "12, button" and nothing about
          what it toggled (WCAG 4.1.2). The count stays the click target; it now
          says what it opens and whether it is open. */}
      <button
        type="button"
        onClick={onToggle}
        title={open ? 'Hide' : 'Show'}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} ${title} (${count})`}
        style={{
          background: 'var(--brand)',
          color: 'white',
          fontSize: 11,
          fontWeight: 700,
          padding: '2px 8px',
          borderRadius: 3,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {count}
      </button>
      {/* A real heading. These are the parish page's sections — "Awaiting your
          approval" above all — and as a styled <span> they were absent from the
          document outline, leaving "With the Registrar" (the section with
          nothing to do) as the only h2 on the page. */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h2>
      {open && right && (
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

/** Registrar-only inline control to move a record to another parish. */
function ReassignControl({
  recordId,
  applicantName,
  parishes,
  onAttempt,
  onDone,
}: {
  recordId: string;
  applicantName: string;
  parishes: { slug: string; name: string }[];
  /** Called as a confirmed move starts, so the page can clear the last notice —
   *  the same text twice is then still a change, and still announced (cca-vz2o). */
  onAttempt: () => void;
  /** Called with what the move said — including a Drive failure (cca-vz2o). */
  onDone: (notice: ReassignNotice) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) {
    return (
      <button className="btn btn-ghost" onClick={() => setOpen(true)}>
        Reassign parish
      </button>
    );
  }

  async function move() {
    if (!slug) return;
    const targetName = parishes.find((p) => p.slug === slug)?.name ?? 'the selected parish';
    // Registrar-only confirmation before a consequential move (updates the record's
    // parish and re-homes the Drive folder).
    if (
      !window.confirm(
        `Move ${applicantName} to ${targetName}?\n\n` +
          `This changes the record's parish and moves the applicant's Drive folder. ` +
          `Any Police result is unaffected.`,
      )
    ) {
      return;
    }
    onAttempt();
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`/api/vetting/registrar/record/${recordId}/reassign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ targetSlug: slug }),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => ({}))) as { error?: string };
        setErr(d.error ?? 'Reassign failed. Please try again.');
      } else {
        const d = (await res.json().catch(() => ({}))) as {
          status?: string;
          message?: string;
          drive?: string;
          driveDetail?: DriveMoveDetail | null;
          folderIdSaved?: boolean;
          auditLogged?: boolean;
        };
        setOpen(false);
        onDone(
          d.status === 'noop'
            ? {
                lead: d.message ?? 'Record is already in that parish.',
                drive: null,
                auditNotLogged: false,
              }
            : {
                lead: `${applicantName} moved to ${targetName}.`,
                drive: driveMoveNote(d.drive, d.driveDetail, d.folderIdSaved),
                // cca-gmdm: the move stands either way; only a strict `false`
                // (the worker's own failure signal) adds the audit note.
                auditNotLogged: d.auditLogged === false,
              },
        );
      }
    } catch {
      setErr('An error occurred. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        className="input"
        style={{ fontSize: 12, padding: '6px 8px', maxWidth: 240 }}
        disabled={busy}
        aria-label="Move record to parish"
      >
        <option value="">Move to parish…</option>
        {parishes.map((p) => (
          <option key={p.slug} value={p.slug}>
            {p.name}
          </option>
        ))}
      </select>
      <button className="btn btn-primary" disabled={!slug || busy} onClick={() => void move()}>
        {busy ? 'Moving…' : 'Move'}
      </button>
      <button
        className="btn btn-ghost"
        disabled={busy}
        onClick={() => {
          setOpen(false);
          setErr(null);
        }}
      >
        Cancel
      </button>
      {err && <span style={{ fontSize: 12, color: 'var(--red)' }}>{err}</span>}
    </div>
  );
}

/** One pipeline section. Module-level (stable identity) so re-rendering the page
 *  — e.g. on each keystroke in the Results search — updates rows in place instead
 *  of remounting the whole list, which was making the search feel very slow. */
function Section({
  title,
  items,
  totalCount,
  line,
  searchControl,
  alwaysShow,
  emptyMessage,
  rowAction,
  open,
  onToggle,
}: {
  title: string;
  items: PipelineItem[];
  /** Badge count when `items` is a filtered subset (e.g. the Results search). */
  totalCount?: number;
  line: (item: PipelineItem) => string;
  searchControl?: JSX.Element | false;
  alwaysShow?: boolean;
  emptyMessage?: string;
  rowAction?: ((item: PipelineItem) => JSX.Element | null) | undefined;
  open: boolean;
  onToggle: () => void;
}): JSX.Element | null {
  if (items.length === 0 && !alwaysShow) return null;
  return (
    <div style={{ marginTop: 28 }}>
      <CollapsibleHead
        title={title}
        count={totalCount ?? items.length}
        open={open}
        onToggle={onToggle}
        right={searchControl}
      />
      {!open ? null : items.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text2)', padding: '4px 0' }}>
          {emptyMessage ?? 'Nothing here.'}
        </p>
      ) : (
        items.map((item) => (
          <div
            key={item.id}
            style={{
              background: 'white',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '14px 20px',
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{item.applicantName}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 10 }}>
                  {item.jobTitle}
                </span>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {item.driveFolderUrl && (
                  <a
                    href={item.driveFolderUrl}
                    target="_blank"
                    rel="noopener"
                    title="Open this applicant's vetting folder"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--brand)',
                      background: 'var(--brand-lt)',
                      border: '1px solid var(--brand)',
                      borderRadius: 999,
                      padding: '1px 9px',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Folder ↗
                  </a>
                )}
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{line(item)}</span>
              </span>
            </div>
            {rowAction && (
              <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                {rowAction(item)}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

/** Post-approval visibility — where this parish's applications sit now. */
function PipelineSections({
  awaitingLodgement,
  awaitingResult,
  results,
  isRegistrar,
  parishes,
  onReassignAttempt,
  onReassigned,
}: {
  awaitingLodgement: PipelineItem[];
  awaitingResult: PipelineItem[];
  results: PipelineItem[];
  isRegistrar: boolean;
  parishes: { slug: string; name: string }[];
  /** Clears the page's notices as a move starts (see ReassignControl.onAttempt). */
  onReassignAttempt: () => void;
  onReassigned: (notice: ReassignNotice) => void;
}): JSX.Element | null {
  const [resultsQuery, setResultsQuery] = useState('');
  const [openLodgement, setOpenLodgement] = useState(true);
  const [openAwaiting, setOpenAwaiting] = useState(true);
  const [openResults, setOpenResults] = useState(true);
  const [resultsSort, setResultsSort] = useState<'expiry' | 'name' | 'received'>('expiry');
  if (!awaitingLodgement.length && !awaitingResult.length && !results.length) return null;
  const rq = resultsQuery.trim().toLowerCase();
  const filteredResults = rq
    ? results.filter((r) => r.applicantName.toLowerCase().includes(rq))
    : results;
  const sortedResults = [...filteredResults].sort(
    resultsSort === 'name'
      ? byName
      : resultsSort === 'received'
        ? (a, b) => ts(b.receivedAt, 0) - ts(a.receivedAt, 0)
        : (a, b) =>
            ts(a.expiresAt, Number.MAX_SAFE_INTEGER) - ts(b.expiresAt, Number.MAX_SAFE_INTEGER),
  );

  // Registrar can move any record (incl. lodged/result) to the correct parish.
  const reassignAction = isRegistrar
    ? (item: PipelineItem) => (
        <ReassignControl
          recordId={item.id}
          applicantName={item.applicantName}
          parishes={parishes}
          onAttempt={onReassignAttempt}
          onDone={onReassigned}
        />
      )
    : undefined;

  return (
    <>
      <Section
        title="With the Registrar — awaiting lodgement"
        items={[...awaitingLodgement].sort(byName)}
        line={(i) => `Approved ${fmtNZDate(i.approvedAt)}`}
        rowAction={reassignAction}
        open={openLodgement}
        onToggle={() => setOpenLodgement((o) => !o)}
      />
      <Section
        title="Lodged with NZ Police — awaiting result"
        items={[...awaitingResult].sort(byName)}
        line={(i) => `Lodged ${fmtNZDate(i.lodgedAt)}`}
        rowAction={reassignAction}
        open={openAwaiting}
        onToggle={() => setOpenAwaiting((o) => !o)}
      />
      <Section
        title="Results"
        items={sortedResults}
        totalCount={results.length}
        alwaysShow={results.length > 0}
        emptyMessage={`No results match “${resultsQuery.trim()}”.`}
        open={openResults}
        onToggle={() => setOpenResults((o) => !o)}
        searchControl={
          results.length > 0 && (
            <>
              <select
                value={resultsSort}
                onChange={(e) => setResultsSort(e.target.value as typeof resultsSort)}
                aria-label="Sort results"
                className="input"
                style={{ width: 'auto', fontSize: 12, padding: '6px 8px' }}
              >
                <option value="expiry">Expiry (soonest first)</option>
                <option value="name">Name (A–Z)</option>
                <option value="received">Received (newest first)</option>
              </select>
              <input
                type="search"
                value={resultsQuery}
                onChange={(e) => setResultsQuery(e.target.value)}
                placeholder="Search by name…"
                aria-label="Search results by name"
                className="input"
                style={{ maxWidth: 220, fontSize: 13, padding: '6px 10px' }}
              />
            </>
          )
        }
        line={(i) =>
          i.cleared
            ? `✓ Cleared ${fmtNZDate(i.receivedAt)} — valid until ${fmtNZDate(i.expiresAt)}`
            : `Result received ${fmtNZDate(i.receivedAt)} — requires Lead Officer review`
        }
        rowAction={reassignAction}
      />
    </>
  );
}
